import { TrendingUp } from "lucide-react";
import { IMPACT } from "@/data/content";
import SectionBadge from "@/components/site/SectionBadge";

/* ------------------------------------------------------------------ */
/* Homepage — 02: Selected Leadership Impact strip (directly under hero) */
/* ------------------------------------------------------------------ */
/* Homepage — 07: Selected Work / Impact                               */
/* ------------------------------------------------------------------ */

const HOME_IMPACT = [
  {
    metric: "5× Sales Growth",
    body: "Helped build and scale a differentiated consumer business to a ₹150Cr+ annual run-rate while reaching a Top 3 Amazon bestseller position.",
  },
  {
    metric: "+66% Sales Growth | +9 pts EBITDA",
    body: "Rebuilt the commercial economics of an e-commerce business while moving EBITDA from -6% to +3%.",
  },
  {
    metric: "First Profit in a Decade",
    body: "Led the turnaround of a consumer-durables category, taking it to profitability for the first time since inception.",
  },
  {
    metric: "4,000+ Locations",
    body: "Led large-scale transformation across an international corporate and franchise network.",
  },
];

export function HomeImpact() {
  return (
    <section
      id="impact"
      data-testid="home-impact-section"
      className="py-16 md:py-24 bg-[#F2F2F2]"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 md:px-12">
        <SectionBadge label="Selected Leadership Impact" />
        <div className="mt-6 grid grid-cols-12 gap-6 md:gap-10 mb-12 md:mb-14">
          <div className="col-span-12 md:col-span-8">
            <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
              Strategy matters when the numbers move.
            </h2>
          </div>
        </div>

        <div className="border-t border-black/10">
          {HOME_IMPACT.map((im, i) => (
            <div
              key={i}
              data-testid={`home-impact-${i}`}
              className="grid grid-cols-12 gap-4 md:gap-10 py-8 md:py-10 border-b border-black/10 reveal"
            >
              <div className="col-span-12 md:col-span-5">
                <div className="font-serif-display text-3xl sm:text-4xl lg:text-[44px] text-black leading-[1.05]">
                  {im.metric}
                </div>
              </div>
              <div className="col-span-12 md:col-span-7 md:flex md:items-center">
                <p className="text-[15px] text-zinc-600 leading-relaxed max-w-xl">
                  {im.body}
                </p>
              </div>
            </div>
          ))}
        </div>

        <p className="mt-8 text-xs sm:text-[13px] text-zinc-500 leading-relaxed max-w-4xl">
          Selected outcomes delivered by BLEZZINGS leadership across prior
          operating and consulting roles. These examples represent leadership
          experience and are not presented as BLEZZINGS client engagements.
        </p>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* Shared Impact section (Leadership page)                             */
/* ------------------------------------------------------------------ */

export default function Impact() {
  return (
    <section
      id="impact"
      data-testid="impact-section"
      className="relative pt-16 md:pt-20 pb-12 md:pb-16 bg-[#F2F2F2]"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 md:px-12">
        <SectionBadge label="Impact" icon={<TrendingUp className="h-3.5 w-3.5" />} />
        <div className="mt-6 grid grid-cols-12 gap-6 md:gap-10 mb-14 md:mb-20">
          <div className="col-span-12 md:col-span-6">
            <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-[1.02]">
              Real transformations. <br />
              Measurable outcomes.
            </h2>
          </div>
          <div className="col-span-12 md:col-span-6 md:flex md:flex-col md:justify-end">
            <p className="text-lg text-zinc-700 leading-relaxed max-w-lg">
              Consulting is judged on results — not slides. Here are four
              businesses transformed under our leadership, each with a proof
              point you can measure.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
          {IMPACT.map((c, i) => (
            <ImpactCard key={c.key} card={c} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}

function ImpactCard({ card, index }) {
  return (
    <article
      data-testid={`impact-card-${card.key}`}
      className="group relative bg-white border border-black/10 p-7 sm:p-9 md:p-10 flex flex-col justify-between overflow-hidden transition-all duration-500 hover:border-black hover:-translate-y-1"
    >
      <div>
        <div className="flex items-start justify-between gap-4">
          <div className="text-[11px] font-semibold tracking-[0.28em] uppercase text-zinc-500 whitespace-pre-line leading-snug">
            {card.category}
          </div>
          <div className="text-[11px] font-semibold tracking-[0.28em] text-zinc-400 tabular">
            {String(index + 1).padStart(2, "0")}
          </div>
        </div>
        <div className="mt-6 flex items-baseline gap-3 flex-wrap min-h-[72px] md:min-h-[96px]">
          <div className="font-serif-display text-5xl sm:text-6xl md:text-6xl text-black leading-none tabular">
            {card.metric}
          </div>
          <div className="text-xs sm:text-sm text-zinc-500 uppercase tracking-[0.2em]">
            {card.metricSub}
          </div>
        </div>
        <h3 className="mt-6 font-serif-display text-2xl md:text-3xl leading-tight text-black">
          {card.title}
        </h3>
        <p className="mt-4 text-[15px] leading-relaxed text-zinc-600 max-w-md">
          {card.body}
        </p>
      </div>

      <div className="mt-8 flex flex-wrap gap-2">
        {card.highlights.map((h) => (
          <span
            key={h}
            className="inline-flex items-center h-7 px-3 border border-black/10 text-xs text-zinc-800 bg-[#F2F2F2]/60"
          >
            {h}
          </span>
        ))}
      </div>

      {/* subtle bottom accent line */}
      <span
        aria-hidden
        className="absolute left-0 bottom-0 h-[3px] w-0 bg-black transition-all duration-500 group-hover:w-full"
      />
    </article>
  );
}
